import { useEffect, useState } from "react";
import { Send, Sparkles, History, FlaskConical, Users as UsersIcon, Megaphone } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const AUDIENCE_OPTIONS = [
  { v: "all", label: "All users" },
  { v: "free", label: "Free users only" },
  { v: "pro_standard", label: "Pro users only" },
  { v: "pro_plus", label: "Pro Plus users only" },
  { v: "admins", label: "Admins only" },
  { v: "specific", label: "Specific emails (comma-separated)" },
];

const APOLOGY_TEMPLATE = {
  subject: "We're sorry about today's outage",
  headline: "Sorry for the disruption",
  body_html:
    "<p>Hi {{first_name}},</p><p>We hit a snag earlier today and Studict was harder to reach than it should have been. " +
    "It's fully resolved now and your data was never at risk.</p><p>Thanks for sticking with us.</p><p>— The Studict team</p>",
};
const PROMO_TEMPLATE = {
  subject: "🎉 Special offer — just for you, {{first_name}}",
  headline: "A little something for you",
  body_html:
    "<p>Hi {{first_name}},</p><p>You've been making the most of Studict on the <strong>{{plan}}</strong> plan. " +
    "Here's a quick promotion to thank you.</p><p>Use code <strong>STUDY20</strong> at checkout for 20% off Pro Plus.</p>",
};

export default function AdminBroadcast() {
  const [form, setForm] = useState({
    subject: APOLOGY_TEMPLATE.subject,
    headline: APOLOGY_TEMPLATE.headline,
    body_html: APOLOGY_TEMPLATE.body_html,
    audience: "all",
    emails: "",
    cta_url: "",
    cta_label: "Open Studict",
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/broadcast/history");
      setHistory(r.data.broadcasts || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const buildPayload = (test_only = false) => ({
    subject: form.subject,
    headline: form.headline,
    body_html: form.body_html,
    audience: form.audience,
    emails: form.audience === "specific" ? form.emails.split(",").map((e) => e.trim()).filter(Boolean) : undefined,
    cta_url: form.cta_url || undefined,
    cta_label: form.cta_label || undefined,
    test_only,
  });

  const doPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/broadcast/preview", buildPayload(false));
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!confirm("Send a test of this email to your own admin address?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/broadcast", buildPayload(true));
      toast.success(r.data.message || "Test sent");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setBusy(false); }
  };

  const sendLive = async () => {
    if (!preview) { toast.error("Click Preview first to check recipient count and rendering."); return; }
    if (!confirm(`Send LIVE to ${preview.recipient_count} recipient(s)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/broadcast", buildPayload(false));
      toast.success(r.data.message || "Broadcast queued");
      setPreview(null);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-5xl">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Admin · Outreach</p>
        <h1 className="font-display text-4xl font-black mt-1 flex items-center gap-3">
          <Megaphone className="w-8 h-8" strokeWidth={2.5}/> Email broadcast
        </h1>
        <p className="text-base font-medium text-slate-600 mt-2">
          Send a personalized email to many users at once. Each recipient sees their own name automatically.
        </p>
        <div className="mt-3 inline-flex flex-wrap items-center gap-2 text-xs bg-yellow-100 border-2 border-slate-900 rounded-md px-3 py-2" style={{ boxShadow: "2px 2px 0 0 #0F172A" }}>
          <strong>Use DOUBLE braces:</strong>
          <code>{"{{first_name}}"}</code> ·
          <code>{"{{name}}"}</code> ·
          <code>{"{{email}}"}</code> ·
          <code>{"{{plan}}"}</code>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setForm({ ...form, ...APOLOGY_TEMPLATE })} data-testid="bc-tpl-apology" className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3">
          <Sparkles className="w-3 h-3" strokeWidth={2.5}/> Apology template
        </button>
        <button onClick={() => setForm({ ...form, ...PROMO_TEMPLATE })} data-testid="bc-tpl-promo" className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3">
          <Sparkles className="w-3 h-3" strokeWidth={2.5}/> Promo template
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); doPreview(); }} className="nb-card p-6 space-y-5">
        <div>
          <label className="block text-xs uppercase tracking-widest font-bold mb-2">Audience</label>
          <select
            data-testid="bc-audience"
            value={form.audience}
            onChange={(e) => setForm({ ...form, audience: e.target.value })}
            className="nb-input"
          >
            {AUDIENCE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          {form.audience === "specific" && (
            <input
              data-testid="bc-emails"
              value={form.emails}
              onChange={(e) => setForm({ ...form, emails: e.target.value })}
              placeholder="alice@example.com, bob@example.com"
              className="nb-input mt-2"
            />
          )}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-bold mb-2">Subject line</label>
          <input
            data-testid="bc-subject"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="nb-input" maxLength={200}
            placeholder="Sorry about today's outage"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-bold mb-2">Headline (shown big in the email)</label>
          <input
            data-testid="bc-headline"
            value={form.headline}
            onChange={(e) => setForm({ ...form, headline: e.target.value })}
            className="nb-input" maxLength={200}
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-bold mb-2">Body (HTML allowed)</label>
          <textarea
            data-testid="bc-body"
            value={form.body_html}
            onChange={(e) => setForm({ ...form, body_html: e.target.value })}
            rows={10}
            className="nb-input font-mono text-sm"
            placeholder="<p>Hi {{first_name}},</p><p>...</p>"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold mb-2">CTA URL (optional)</label>
            <input
              data-testid="bc-cta-url"
              value={form.cta_url}
              onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
              placeholder="https://studictsmartdictionary.com/dashboard"
              className="nb-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold mb-2">CTA label</label>
            <input
              data-testid="bc-cta-label"
              value={form.cta_label}
              onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
              className="nb-input"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={busy} data-testid="bc-preview-btn" className="nb-btn-primary inline-flex items-center gap-2">
            <UsersIcon className="w-4 h-4" strokeWidth={2.5}/> Preview & count
          </button>
          <button type="button" onClick={sendTest} disabled={busy} data-testid="bc-test-btn" className="nb-btn-secondary inline-flex items-center gap-2">
            <FlaskConical className="w-4 h-4" strokeWidth={2.5}/> Send test to me
          </button>
          {preview && (
            <button type="button" onClick={sendLive} disabled={busy} data-testid="bc-send-btn" className="nb-btn-primary inline-flex items-center gap-2 !bg-emerald-300">
              <Send className="w-4 h-4" strokeWidth={2.5}/> Send to {preview.recipient_count} recipient{preview.recipient_count !== 1 ? "s" : ""}
            </button>
          )}
        </div>
      </form>

      {preview && (
        <div className="nb-card p-6 mt-6 bg-yellow-50" data-testid="bc-preview">
          <h2 className="font-display text-xl font-black mb-3">Preview (rendered for your admin account)</h2>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Subject</div>
          <div className="font-bold mb-3">{preview.preview_subject}</div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Body</div>
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: preview.preview_body_html }}/>
          <p className="text-xs text-slate-500 mt-4">Each recipient will see their own name automatically.</p>
        </div>
      )}

      <h2 className="font-display text-2xl font-black mt-10 mb-3 flex items-center gap-2">
        <History className="w-5 h-5" strokeWidth={2.5}/> Recent broadcasts
      </h2>
      <div className="space-y-2">
        {history.length === 0 && <p className="text-sm text-slate-500">No broadcasts yet.</p>}
        {history.map((b) => (
          <div key={b.id} className="nb-card-sm p-3 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid={`bc-history-${b.id}`}>
            <div>
              <div className="font-bold">{b.subject}</div>
              <div className="text-xs text-slate-500">{b.audience} · {new Date(b.created_at).toLocaleString()}</div>
            </div>
            <div className="text-xs font-bold">
              {b.status === "running" ? <span className="text-amber-700">sending… {b.sent_count}/{b.recipient_count}</span> : <span className="text-emerald-700">sent {b.sent_count} · failed {b.failed_count}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
